import React, { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';

const ACTIVITY_KEY = 'rtr_last_activity';
const HEARTBEAT_KEY = 'rtr_last_heartbeat';
const HEARTBEAT_EVERY_MS = 60 * 1000;
const WARN_BEFORE_MS = 2 * 60 * 1000;
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('rtr-auth') : null;

export const broadcastLogout = reason => channel?.postMessage({ type: 'logout', reason });
export const onBroadcastLogout = cb => {
  if (!channel) return () => {};
  const h = e => { if (e.data?.type === 'logout') cb(e.data.reason); };
  channel.addEventListener('message', h);
  return () => channel.removeEventListener('message', h);
};

const now = () => Date.now();
const readNum = k => Number(localStorage.getItem(k) || 0);
const markActive = () => localStorage.setItem(ACTIVITY_KEY, String(now()));
const fmt = ms => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export function SessionGuard({ api, session, onTimeout }) {
  const idleMs = (session?.idle_minutes || 30) * 60 * 1000;
  const expiresAt = session?.expires_at ? new Date(session.expires_at).getTime() : 0;
  const [left, setLeft] = useState(null);
  const [kind, setKind] = useState(null);
  const fired = useRef(false);
  const warning = useRef(false);
  warning.current = kind === 'idle';

  useEffect(() => {
    markActive();
    let lastWrite = 0;
    const onAct = () => {
      if (warning.current || now() - lastWrite < 5000) return;
      lastWrite = now();
      markActive();
    };
    const evts = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    evts.forEach(e => window.addEventListener(e, onAct, { passive: true }));
    return () => evts.forEach(e => window.removeEventListener(e, onAct));
  }, []);

  useEffect(() => {
    const tick = async () => {
      const t = now();
      const last = readNum(ACTIVITY_KEY);
      const idleLeft = last + idleMs - t;
      const expLeft = expiresAt ? expiresAt - t : Infinity;
      if ((idleLeft <= 0 || expLeft <= 0) && !fired.current) {
        fired.current = true;
        onTimeout(idleLeft <= 0 ? 'idle' : 'expired');
        return;
      }
      if (document.hidden) return;  // timeouts still enforced; skip UI + heartbeat while hidden
      if (idleLeft <= WARN_BEFORE_MS) { setKind('idle'); setLeft(idleLeft); }
      else if (expLeft <= 5 * 60 * 1000) { setKind('expiry'); setLeft(expLeft); }
      else { setKind(null); setLeft(null); }
      const lastBeat = readNum(HEARTBEAT_KEY);
      if (last > lastBeat && t - lastBeat >= HEARTBEAT_EVERY_MS) {
        localStorage.setItem(HEARTBEAT_KEY, String(t));
        api.post('/auth/heartbeat').catch(() => {});
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [api, idleMs, expiresAt, onTimeout]);

  if (!kind) return null;
  const stay = () => {
    markActive();
    localStorage.setItem(HEARTBEAT_KEY, String(now()));
    api.post('/auth/heartbeat').catch(() => {});
  };
  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="session-warning-overlay">
      <div className="bg-white rounded-md shadow-xl max-w-sm w-full p-6 border border-[#E2E2DF]">
        <div className="flex items-center gap-2 text-[#D35400] mb-2"><Clock size={18} /><h3 className="font-semibold text-slate-900">
          {kind === 'idle' ? 'Are you still there?' : 'Session ending soon'}</h3></div>
        <p className="text-sm text-slate-600 mb-4" data-testid="session-warning-text">
          {kind === 'idle'
            ? <>You'll be logged out for inactivity in <b data-testid="session-warning-countdown">{fmt(left)}</b>.</>
            : <>Your 4-hour session ends in <b data-testid="session-warning-countdown">{fmt(left)}</b>. Save your work — you'll need to log in again.</>}
        </p>
        <div className="flex gap-2 justify-end">
          <button data-testid="session-warning-logout-btn" onClick={() => onTimeout('')} className="px-4 py-2 text-sm rounded-md border border-[#E2E2DF] text-slate-700 hover:bg-slate-50 transition-colors">Log out now</button>
          {kind === 'idle' && <button data-testid="session-warning-stay-btn" onClick={stay} className="px-4 py-2 text-sm rounded-md bg-[#D35400] hover:bg-[#A04000] text-white font-semibold transition-colors">Stay logged in</button>}
        </div>
      </div>
    </div>
  );
}
